import { X } from 'lucide-react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui'
import { Combobox } from 'src/shared/components/Combobox'
import { Switch } from 'src/shared/components/ui/switch'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import type { RequestSegment } from '../requestNavParams'

export const RequestsAdminFilters = ({
  listSegment,
  statusFilter,
  setStatusFilter,
  selectedQuote,
  setSelectedQuote,
  selectedOrg,
  setSelectedOrg,
  setOrgSearchInput,
  hasItemsOnly,
  setHasItemsOnly,
  excludeAnonOrgs,
  setExcludeAnonOrgs,
  hasActiveFilters,
  onReset,
  quoteOptions,
  orgOptions,
  statusOptions,
}: {
  listSegment: RequestSegment
  statusFilter: 'all' | string
  setStatusFilter: (value: 'all' | string) => void
  selectedQuote: string
  setSelectedQuote: (value: string) => void
  selectedOrg: string
  setSelectedOrg: (value: string) => void
  orgSearchInput: string
  setOrgSearchInput: (value: string) => void
  hasItemsOnly: boolean
  setHasItemsOnly: (value: boolean) => void
  excludeAnonOrgs: boolean
  setExcludeAnonOrgs: (value: boolean) => void
  hasActiveFilters: boolean
  onReset: () => void
  quoteOptions: { value: string; label: string }[]
  orgOptions: { value: string; label: string }[]
  statusOptions: { value: string; label: string }[]
}) => {
  const { t } = useLocalization()

  return (
    <Row className="gap-3 items-center mt-4 bg-white/70 p-1 rounded-xl border border-gray-200">
      <Column className="w-[180px] mb-0">
        <Combobox
          ariaLabel={t('Status filter', 'Statusfilter')}
          value={statusFilter}
          onValueChange={(next) => setStatusFilter(next === '' ? 'all' : next)}
          options={statusOptions}
          placeholder={t('Filter by status...', 'Filtrera efter status...')}
          searchPlaceholder={t('Search status...', 'Sök status...')}
          emptyMessage={t('No statuses found.', 'Inga status hittades.')}
          clearOnReselect
          buttonVariant="ghost"
          buttonClassName="font-normal"
          disabled={listSegment === 'drafts'}
        />
      </Column>

      <Column className="w-[180px] mb-0">
        <Combobox
          ariaLabel={t('Request filter', 'Förfrågningsfilter')}
          value={selectedQuote}
          onValueChange={(next) => setSelectedQuote(next)}
          options={quoteOptions}
          placeholder={t('Filter by request...', 'Filtrera efter förfrågan...')}
          searchPlaceholder={t('Search name or ID...', 'Sök namn eller ID...')}
          emptyMessage={t(
            'No requests found.',
            'Inga förfrågningar hittades.'
          )}
          clearOnReselect
          buttonVariant="ghost"
          buttonClassName="font-normal"
        />
      </Column>

      <Column className="w-[180px] mb-0">
        <Combobox
          ariaLabel={t('Organization filter', 'Organisationsfilter')}
          value={selectedOrg}
          onValueChange={(next) => setSelectedOrg(next)}
          options={orgOptions}
          onSearchChange={setOrgSearchInput}
          placeholder={t(
            'Filter by organization...',
            'Filtrera efter organisation...'
          )}
          searchPlaceholder={t(
            'Search organization...',
            'Sök organisation...'
          )}
          emptyMessage={t(
            'No organizations found.',
            'Inga organisationer hittades.'
          )}
          clearOnReselect
          buttonVariant="ghost"
          buttonClassName="font-normal"
        />
      </Column>

      <Row className="gap-2 items-center ml-2">
        <Switch
          id="requests-has-items"
          size="sm"
          checked={hasItemsOnly}
          onCheckedChange={setHasItemsOnly}
        />
        <label
          htmlFor="requests-has-items"
          className="text-sm cursor-pointer text-muted-foreground"
        >
          {t('No empty requests', 'Inga tomma förfrågningar')}
        </label>

        <Switch
          id="requests-exclude-anon-orgs"
          size="sm"
          checked={excludeAnonOrgs}
          onCheckedChange={setExcludeAnonOrgs}
        />
        <label
          htmlFor="requests-exclude-anon-orgs"
          className="text-sm cursor-pointer text-muted-foreground"
        >
          {t('No anon orgs', 'Inga anonyma orgs')}
        </label>
      </Row>

      {hasActiveFilters && (
        <Button variant="ghost" size="sm" onClick={onReset} className="ml-auto">
          <X size={16} strokeWidth={2} className="mr-1 shrink-0" />
          {t('Reset filters', 'Återställ filter')}
        </Button>
      )}
    </Row>
  )
}
